import { useState } from "react";

import classnames from "classnames";
import { MenuHorizontal } from "neetoicons";
import { Typography, Dropdown, Button, Tag } from "neetoui";
import { is } from "ramda";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import CheckPointNavLinks from "./CheckPointNavLink.jsx";
import PinIcon from "./PinIcon.jsx";
import UnpinIcon from "./UnpinIcon.jsx";

import { filterByPermissions } from "../../utils";

const MoreDropdown = ({ moreNavLinks, isNavLinkPinned, handleTogglePin }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dropdown
      {...{ isOpen }}
      closeOnSelect
      appendTo={() => document.body}
      position="right"
      strategy="fixed"
      zIndex={99999}
      customTarget={() => (
        <div
          data-testid="more-nav-links-dropdown-button"
          className={classnames(
            "neeto-ui-flex neeto-ui-items-center neeto-ui-no-underline neeto-ui-select-none neeto-molecules-sidebar__link neeto-ui-w-full neeto-ui-cursor-pointer",
            { "neeto-molecules-sidebar__link--hover-active": isOpen }
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="neeto-molecules-sidebar__link-icon">
            <MenuHorizontal />
          </span>
          <Typography
            className="neeto-molecules-sidebar__link-label"
            component="span"
            style="body2"
          >
            {t("neetoMolecules.sidebar.more")}
          </Typography>
        </div>
      )}
      onClickOutside={() => setIsOpen(false)}
      onClose={() => setIsOpen(false)}
    >
      <Dropdown.Menu
        className="p-0"
        data-testid="more-nav-links-dropdown-menu"
        position="right"
        strategy="fixed"
      >
        {moreNavLinks.filter(filterByPermissions).map((navLink, index) => {
          const {
            label,
            icon,
            key: navLinkKey,
            to,
            onClick,
            tag,
            ...otherProps
          } = navLink;
          const IconSVG = icon;
          const isPinned = isNavLinkPinned(navLink);
          const url = new URL(to, window.location.href);
          const isActive = () =>
            is(Function, otherProps.isActive)
              ? otherProps.isActive()
              : location.pathname.startsWith(url.pathname);

          return (
            <Dropdown.MenuItem data-testid={label} key={navLinkKey || index}>
              <CheckPointNavLinks
                {...{ onClick, to }}
                className={classnames(
                  "flex cursor-pointer items-center justify-between gap-2 px-2 py-1",
                  { "neeto-ui-bg-accent-100 font-semibold": isActive() }
                )}
              >
                <span className="flex min-w-0 flex-grow items-center gap-2">
                  {icon && <IconSVG className="flex-shrink-0" size={20} />}
                  <Typography
                    className="flex min-w-0 flex-grow items-center gap-2"
                    component="span"
                    style="body2"
                  >
                    {label}
                    {tag && <Tag {...tag} />}
                  </Typography>
                </span>
                <Button
                  className="flex-shrink-0"
                  data-testid="pin-button"
                  size="small"
                  style="text"
                  icon={
                    isPinned
                      ? () => <UnpinIcon otherProps={{}} size={16} />
                      : () => <PinIcon otherProps={{}} size={16} />
                  }
                  tooltipProps={{
                    content: isPinned
                      ? t("neetoMolecules.sidebar.unpin")
                      : t("neetoMolecules.sidebar.pin"),
                    position: "right",
                  }}
                  onClick={event => handleTogglePin(navLink, event)}
                />
              </CheckPointNavLinks>
            </Dropdown.MenuItem>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MoreDropdown;
